export const DEFAULT_QUIZ_RULES_HTML = `<h2>Quiz Rules</h2>
<p>Please read the following instructions carefully before starting:</p>
<ol>
  <li><strong>No Cheating</strong><br/>Any form of cheating is strictly prohibited, including capturing content using phones, screenshots, or other devices.</li>
  <li><strong>No External Assistance</strong><br/>You must complete the quiz independently without using notes, resources, or help from others.</li>
  <li><strong>Academic Integrity</strong><br/>All participants are expected to follow academic integrity guidelines at all times.</li>
  <li><strong>Time Limit</strong><br/>Each quiz must be completed within the allocated time. The timer runs continuously and will not pause, even if you leave the app or exit the quiz.</li>
  <li><strong>Auto-Submission</strong><br/>The quiz will be automatically submitted once the time expires.</li>
  <li><strong>No Backtracking</strong><br/>Once a question is answered, you cannot return to it.</li>
  <li><strong>Single Attempt</strong><br/>Each quiz can only be taken once unless specified otherwise.</li>
  <li><strong>Full Attention Required</strong><br/>Leaving the quiz screen may affect your progress or lead to automatic submission.</li>
  <li><strong>Stable Connection</strong><br/>Ensure you have a reliable internet connection before starting the quiz.</li>
  <li><strong>Question Format</strong><br/>Questions may include text, images, histology slides, or 3D models, and are primarily multiple-choice.</li>
</ol>
<p><em>By starting this quiz, you confirm that you understand and agree to these rules.</em></p>`;

export function emptyQuizDraft() {
  return {
    title: "",
    time_limit_minutes: 30,
    shuffle_questions: true,
    shuffle_answers: true,
    show_score_immediately: true,
    show_answers_after: true,
    rules_html: DEFAULT_QUIZ_RULES_HTML,
    topic_rules: [],
    bank_preference: null,
    awaiting_confirmation: false,
    confirmation_fingerprint: null,
  };
}

function hasMarksOrResultsKeywords(t) {
  return /\b(results?|scores?|marks?|grades?|performance|averages?|analytics|analysis|progress|attempts?|performed)\b/.test(
    t
  );
}

function isRosterDirectoryAsk(t) {
  if (hasMarksOrResultsKeywords(t)) return false;
  return (
    /\b(users?|roster|directory|user details?)\b/.test(t) ||
    /\bauthorized scope\b/.test(t) ||
    /\bstudents? (in|of|at) (my |the )?universit/.test(t) ||
    /\busers? (in|of|at) (my |the )?universit/.test(t) ||
    (/\b(list|show|tell|who|details?|about)\b/.test(t) &&
      /\b(students?|professors?|users?)\b/.test(t) &&
      !/\b(classes?)\b/.test(t))
  );
}

/** Heuristic: user wants student quiz results / marks / performance analysis (not create). */
export function isQuizResultsIntent(text) {
  const t = String(text || "").toLowerCase().trim();
  if (!t) return false;
  const createOnly =
    /\bcreate\b/.test(t) &&
    /\bquiz\b/.test(t) &&
    !hasMarksOrResultsKeywords(t);
  if (createOnly) return false;
  // Roster / user-details asks are handled by isQuizUsersIntent.
  if (isRosterDirectoryAsk(t)) return false;
  return (
    hasMarksOrResultsKeywords(t) ||
    /\b(check|show|tell|analyze|analyse|summarize|summarise)\b[\s\S]{0,40}\b(students?|classes?|attempts?)\b/.test(
      t
    ) ||
    /\bstudents?\b[\s\S]{0,40}\b(results?|scores?|marks?|grades?|performed|performance)\b/.test(
      t
    ) ||
    /show the results|check quiz results|^my_results$/.test(t)
  );
}

/**
 * Heuristic: user wants university/class user roster or user details
 * (not marks/results — those use isQuizResultsIntent).
 */
export function isQuizUsersIntent(text) {
  const t = String(text || "").toLowerCase().trim();
  if (!t) return false;
  return isRosterDirectoryAsk(t);
}

/** Heuristic: message looks like quiz-copilot intent. */
export function isQuizCopilotIntent(text) {
  const t = String(text || "").toLowerCase();
  if (!t.trim()) return false;
  if (isQuizResultsIntent(t) || isQuizUsersIntent(t)) return true;
  if (/\b(delete|remove|erase|destroy|purge)\b/.test(t) && /\b(quiz|question|title)\b/.test(t)) {
    return true; // still route to copilot so it can refuse securely
  }
  return (
    /\bquiz\b/.test(t) ||
    /\bquestion bank\b/.test(t) ||
    /\btime limit\b/.test(t) ||
    /\b(histology|embryology|anatomy)\b/.test(t) ||
    /^(cat:|topic:|bank:|start_quiz)/.test(t.trim()) ||
    /^(yes|confirm|create quiz|looks good)\b/.test(t.trim())
  );
}

export function draftToQuizForm(draft) {
  const d = draft || emptyQuizDraft();
  return {
    title: d.title || "",
    time_limit_minutes: d.time_limit_minutes ?? 30,
    shuffle_questions: d.shuffle_questions !== false,
    shuffle_answers: d.shuffle_answers !== false,
    show_score_immediately: d.show_score_immediately !== false,
    show_answers_after: d.show_answers_after !== false,
    rules_html: d.rules_html || DEFAULT_QUIZ_RULES_HTML,
    topic_rules:
      Array.isArray(d.topic_rules) && d.topic_rules.length
        ? d.topic_rules.map((r) => ({
            professor_topic_id: r.professor_topic_id || "",
            bank_source: r.bank_source === "microlab" ? "microlab" : "private",
            catalogue: r.catalogue || "",
            quiz_title_ids: Array.isArray(r.quiz_title_ids) ? r.quiz_title_ids : [],
            num_questions: r.num_questions || 1,
            difficulty_filter: r.difficulty_filter || "",
          }))
        : [
            {
              professor_topic_id: "",
              bank_source: "private",
              catalogue: "",
              quiz_title_ids: [],
              num_questions: 2,
              difficulty_filter: "",
            },
          ],
  };
}

export function quizFormToDraft(form, prev = emptyQuizDraft()) {
  return {
    ...prev,
    title: form.title || "",
    time_limit_minutes: form.time_limit_minutes ?? 30,
    shuffle_questions: form.shuffle_questions !== false,
    shuffle_answers: form.shuffle_answers !== false,
    show_score_immediately: form.show_score_immediately !== false,
    show_answers_after: form.show_answers_after !== false,
    rules_html: form.rules_html || DEFAULT_QUIZ_RULES_HTML,
    topic_rules: Array.isArray(form.topic_rules) ? form.topic_rules : [],
  };
}
